import React, { useEffect, useState } from "react";
import { Box, Grid, Card, CardContent, Typography, Chip, Avatar, LinearProgress, Skeleton, Tooltip } from "@mui/material";
import { collection, onSnapshot, query, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { TIER_COLORS, STATUS_COLORS } from "../theme";
import { Work, AttachMoney, People, CheckCircle, OpenInNew, Timer, HowToReg, ArrowForward } from "@mui/icons-material";

// Returns elapsed minutes since a Firestore Timestamp (or raw seconds field)
function elapsedMinutes(ts: any): number | null {
  if (!ts) return null;
  try {
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return (Date.now() - date.getTime()) / 60000;
  } catch { return null; }
}

function DispatchTimer({ job }: { job: any }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(id);
  }, []);

  if (job.status !== "dispatched") return null;
  const ts = job.dispatchedAt || job["timeline.dispatchedAt"];
  const mins = elapsedMinutes(ts);
  if (mins === null || mins < 1) return null;

  const isRed    = mins >= 5;
  const isAmber  = mins >= 3;
  const color    = isRed ? "#ef4444" : isAmber ? "#f59e0b" : "#6b7280";
  const label    = `${Math.floor(mins)}m waiting`;

  return (
    <Tooltip title={isRed ? "⚠️ Rematch triggered — customer notified" : isAmber ? "Dispatch warning sent" : "Waiting for acceptance"}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, color }}>
        <Timer sx={{ fontSize: 13 }} />
        <Typography variant="caption" fontWeight={700} sx={{ color }}>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export interface DashboardViewProps {
  onJobClick?: (jobId: string) => void;
}

interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
}

function StatCard({ label, value, sub, icon, color, loading }: StatCardProps) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
              {label}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={40} />
            ) : (
              <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>
                {value}
              </Typography>
            )}
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
          <Box sx={{ bgcolor: `${color}22`, borderRadius: 2, p: 1.5, color }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export function DashboardView({ onJobClick }: DashboardViewProps = {}) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [techs, setTechs] = useState<any[]>([]);
  const [techMap, setTechMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  // Waitlist summary state
  const [waitlist, setWaitlist] = useState<any[]>([]);

  useEffect(() => {
    const unsub1 = onSnapshot(
      query(collection(db, "jobs"), orderBy("createdAt", "desc"), limit(50)),
      (snap) => { setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); }
    );
    const unsub2 = onSnapshot(
      collection(db, "technicians"),
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setTechs(list);
        const map: Record<string, string> = {};
        list.forEach((t: any) => { map[t.id] = t.displayName || t.name || t.id.slice(0, 8); });
        setTechMap(map);
      }
    );
    const unsub3 = onSnapshot(
      collection(db, "waitlist"),
      (snap) => { setWaitlist(snap.docs.map(d => ({ id: d.id, ...d.data() }))); }
    );
    return () => { unsub1(); unsub2(); unsub3(); };
  }, []);

  const ACTIVE_STATUSES = ["pending", "matched", "dispatched", "accepted", "en_route", "arrived", "in_progress"];
  const activeJobs = jobs.filter(j => ACTIVE_STATUSES.includes(j.status));
  const todayJobs = jobs.filter(j => {
    const ts = j.createdAt as Timestamp;
    if (!ts) return false;
    const d = ts.toDate();
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth();
  });
  const onlineTechs = techs.filter(t => t.isOnline);
  const todayRevenue = todayJobs
    .filter(j => j.status === "completed")
    .reduce((sum, j) => sum + (j.platformFee || 0), 0);

  const tierCounts = techs.reduce((acc: Record<string, number>, t) => {
    acc[t.tier] = (acc[t.tier] || 0) + 1;
    return acc;
  }, {});

  // Waitlist summary
  const wlTotal = waitlist.length;
  const wlCustomers = waitlist.filter(e => e.role === "customer").length;
  const wlTechnicians = waitlist.filter(e => e.role === "technician").length;
  const wlZipMap: Record<string, number> = {};
  waitlist.forEach(e => { const z = e.zipCode?.trim(); if (z) wlZipMap[z] = (wlZipMap[z] || 0) + 1; });
  const wlTopZips = Object.entries(wlZipMap).sort((a, b) => b[1] - a[1]).slice(0, 3);

  return (
    <Box>
      {/* Stat cards */}
      <Grid container spacing={3} mb={3}>
        <Grid size={{ xs:12, sm:6, md:3 }}>
          <StatCard label="Active Jobs" value={activeJobs.length} sub="right now" icon={<Work />} color="#8b5cf6" loading={loading} />
        </Grid>
        <Grid size={{ xs:12, sm:6, md:3 }}>
          <StatCard label="Techs Online" value={`${onlineTechs.length}/${techs.length}`} sub="available now" icon={<People />} color="#22c55e" loading={loading} />
        </Grid>
        <Grid size={{ xs:12, sm:6, md:3 }}>
          <StatCard label="Today's Jobs" value={todayJobs.length} sub="total requests" icon={<CheckCircle />} color="#3b82f6" loading={loading} />
        </Grid>
        <Grid size={{ xs:12, sm:6, md:3 }}>
          <StatCard label="Today's Revenue" value={`$${todayRevenue.toFixed(2)}`} sub="platform fees" icon={<AttachMoney />} color="#FF8C00" loading={loading} />
        </Grid>
      </Grid>

      {/* Waitlist Summary */}
      <Card sx={{ mb: 3, border: "1px solid rgba(102,51,153,0.25)", background: "linear-gradient(135deg, rgba(102,51,153,0.08) 0%, transparent 100%)" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              <Box sx={{ bgcolor: "rgba(102,51,153,0.2)", borderRadius: 2, p: 1.5, color: "#a78bfa" }}>
                <HowToReg />
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  Waitlist
                </Typography>
                <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, color: "#a78bfa" }}>
                  {wlTotal}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {wlCustomers} customer{wlCustomers !== 1 ? "s" : ""} · {wlTechnicians} technician{wlTechnicians !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </Box>

            {wlTopZips.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5} display="block" mb={0.75}>
                  Top Markets
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {wlTopZips.map(([zip, count]) => (
                    <Chip
                      key={zip}
                      label={`${zip} · ${count}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(102,51,153,0.18)",
                        color: "#a78bfa",
                        fontWeight: 700,
                        fontFamily: "monospace",
                        fontSize: "0.72rem",
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography
                component="a"
                href="#waitlist"
                onClick={(e: React.MouseEvent) => e.preventDefault()}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  color: "#663399",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  textDecoration: "none",
                  cursor: "pointer",
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 1.5,
                  border: "1px solid rgba(102,51,153,0.35)",
                  "&:hover": { bgcolor: "rgba(102,51,153,0.12)", borderColor: "#663399" },
                  transition: "all 0.15s",
                }}
              >
                View Waitlist <ArrowForward sx={{ fontSize: 16 }} />
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Active Jobs Feed */}
        <Grid size={{ xs:12, md:7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                🔴 Live Jobs
              </Typography>
              {loading ? (
                [1,2,3].map(i => <Skeleton key={i} height={60} sx={{ mb: 1 }} />)
              ) : activeJobs.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                  <Typography>No active jobs right now</Typography>
                </Box>
              ) : (
                activeJobs.slice(0, 8).map((job) => (
                  <Tooltip key={job.id} title={onJobClick ? "Click to manage this job" : ""} placement="left">
                    <Box
                      onClick={() => onJobClick?.(job.id)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 2,
                        py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.05)",
                        borderRadius: 1, px: 1, mx: -1,
                        cursor: onJobClick ? "pointer" : "default",
                        transition: "background 0.15s",
                        "&:hover": onJobClick ? { bgcolor: "rgba(139,92,246,0.08)" } : {},
                      }}
                    >
                      <Chip
                        label={job.status?.replace(/_/g, " ") || "pending"}
                        size="small"
                        sx={{ bgcolor: STATUS_COLORS[job.status] || "#666", color: "white", fontWeight: 700, minWidth: 90 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {job.category?.replace(/_/g, " ") || "General"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {job.customerId?.slice(0, 8)}… → {( job.technicianId || job.techId) ? (job.techName || techMap[job.technicianId] || techMap[job.techId] || (job.technicianId || job.techId).slice(0,8)+"…") : <span style={{ color: "#f59e0b" }}>Unassigned</span>}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {job.isEmergency ? "🚨" : "📋"}
                        </Typography>
                        <DispatchTimer job={job} />
                        {onJobClick && <OpenInNew sx={{ fontSize: 12, color: "text.disabled" }} />}
                      </Box>
                    </Box>
                  </Tooltip>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Tech Fleet */}
        <Grid size={{ xs:12, md:5 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                👷 Tech Fleet
              </Typography>
              {(["diamond","gold","silver","bronze"] as const).map(tier => (
                <Box key={tier} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                  <Typography variant="body2" sx={{ color: TIER_COLORS[tier], fontWeight: 700, width: 70, textTransform: "capitalize" }}>
                    {tier}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={techs.length ? ((tierCounts[tier] || 0) / techs.length) * 100 : 0}
                    sx={{ flex: 1, height: 8, borderRadius: 4, bgcolor: "rgba(255,255,255,0.08)", "& .MuiLinearProgress-bar": { bgcolor: TIER_COLORS[tier] } }}
                  />
                  <Typography variant="body2" fontWeight={700} width={20}>
                    {tierCounts[tier] || 0}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>

          {/* Online Techs */}
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} mb={2}>
                🟢 Online Now
              </Typography>
              {loading ? (
                [1,2,3].map(i => <Skeleton key={i} height={50} sx={{ mb: 1 }} />)
              ) : onlineTechs.length === 0 ? (
                <Typography color="text.secondary" variant="body2">No techs online</Typography>
              ) : (
                onlineTechs.map((tech) => (
                  <Box key={tech.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main", fontSize: 14 }}>
                      {tech.displayName?.[0] || "T"}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>{tech.displayName}</Typography>
                      <Typography variant="caption" sx={{ color: TIER_COLORS[tech.tier] || "text.secondary" }}>
                        {tech.tier} · ${tech.hourlyRate}/hr
                      </Typography>
                    </Box>
                    <Chip label={tech.currentJobId ? "Busy" : "Ready"} size="small"
                      color={tech.currentJobId ? "warning" : "success"} variant="outlined" />
                  </Box>
                ))
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
